import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/Badges";
import { dateLabel } from "@/lib/format";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");
export const accOf = (ln) => ln.accepted_qty ?? ln.qty ?? 0;

const GrnLines = ({ g }) => (
  <table className="data-table text-xs">
    <thead><tr><th>SKU</th><th>Item</th><th>Received</th><th>Rejected</th><th>Accepted</th><th>Remarks</th></tr></thead>
    <tbody>{g.lines.map((l) => (
      <tr key={l.product_id}><td className="font-mono">{l.sku}</td><td className="wrap-cell product-name min-w-[240px]">{l.name}</td>
        <td className="font-mono">{l.received_qty ?? l.qty}</td><td className="font-mono text-red-500">{l.rejected_qty || ""}</td>
        <td className="font-mono font-bold text-emerald-600">{accOf(l)}</td><td className="wrap-cell">{l.remarks}</td></tr>))}</tbody>
  </table>
);

export const ReceiptHistory = ({ receipts, onCancel, canEdit }) => {
  const [open, setOpen] = useState({});
  if (!receipts.length) return <div className="text-sm text-muted-foreground" data-testid="po-receipts-empty">No receipts yet</div>;
  return (
    <div className="overflow-x-auto"><table className="data-table" data-testid="po-receipts-table">
      <thead><tr><th></th><th>GRN no</th><th>Date</th><th>Challan / invoice</th><th>WH</th><th>Accepted</th><th>Received by</th><th>Status</th><th></th></tr></thead>
      <tbody>{receipts.map((g) => (
        <Fragment key={g.id}>
          <tr data-testid={`grn-row-${g.receipt_no}`} className={g.status === "cancelled" ? "opacity-60" : ""}>
            <td><button onClick={() => setOpen((s) => ({ ...s, [g.id]: !s[g.id] }))} className="p-1" data-testid={`grn-toggle-${g.receipt_no}`}>{open[g.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button></td>
            <td className="font-mono font-bold text-primary">{g.receipt_no}</td>
            <td className="text-xs">{g.is_opening ? <span className="text-amber-600" data-testid={`grn-opening-${g.receipt_no}`}>Opening balance (date not recorded)</span> : dateLabel(g.date)}</td>
            <td className="text-xs">{[g.challan_no && `Ch ${g.challan_no}`, g.invoice_no && `Inv ${g.invoice_no}`].filter(Boolean).join(" · ") || "—"}</td>
            <td className="text-xs">{g.warehouse}</td>
            <td className="font-mono font-semibold">{g.lines.reduce((s, l) => s + accOf(l), 0)}</td>
            <td className="text-xs">{g.received_by}<div className="text-muted-foreground">{when(g.created_at)}</div></td>
            <td><StatusBadge value={g.status || "active"} />{g.status === "cancelled" && <div className="wrap-cell text-[10px] text-muted-foreground">{g.cancel_reason} · {g.cancelled_by}</div>}</td>
            <td>{canEdit && g.status !== "cancelled" && <Button size="sm" variant="outline" onClick={() => onCancel(g)} data-testid={`grn-cancel-${g.receipt_no}`}>Cancel GRN</Button>}</td>
          </tr>
          {open[g.id] && <tr><td colSpan={9} className="wrap-cell bg-muted/30">
            <GrnLines g={g} />
            {(g.note || g.vehicle_no || g.excess_reason) && <div className="mt-2 text-xs text-muted-foreground">{[g.vehicle_no && `Vehicle ${g.vehicle_no}`, g.note, g.excess_reason && `Excess: ${g.excess_reason}`].filter(Boolean).join(" · ")}</div>}
          </td></tr>}
        </Fragment>))}</tbody>
    </table></div>
  );
};

export const ReceiptMatrix = ({ order, receipts }) => {
  const active = receipts.filter((g) => g.status === "active");
  const hasOpening = active.some((g) => g.is_opening);
  const dates = [...new Set(active.filter((g) => !g.is_opening).map((g) => g.date))].sort();
  const cell = (pid, pred) => active.filter(pred).reduce((s, g) => s + g.lines.filter((l) => l.product_id === pid).reduce((a, l) => a + accOf(l), 0), 0) || "";
  return (
    <div className="overflow-x-auto"><table className="data-table" data-testid="po-matrix">
      <thead><tr><th>Item</th><th>Ordered</th>{hasOpening && <th>Opening</th>}{dates.map((d) => <th key={d}>{dateLabel(d)}</th>)}<th>Pending</th></tr></thead>
      <tbody>{order.items.map((i) => (
        <tr key={i.product_id}><td className="wrap-cell product-name min-w-[240px]">{i.name}</td><td className="font-mono">{i.qty}</td>
          {hasOpening && <td className="font-mono">{cell(i.product_id, (g) => g.is_opening)}</td>}
          {dates.map((d) => <td key={d} className="font-mono">{cell(i.product_id, (g) => !g.is_opening && g.date === d)}</td>)}
          <td className="font-mono font-semibold">{Math.max(i.qty - (i.received || 0) - (i.short_closed_qty || 0), 0)}</td></tr>))}</tbody>
    </table></div>
  );
};
